import { useState, useEffect, useCallback, useRef } from "react";
import Select from "react-select";
import LinePlot from "./LinePlot";
import { resetState, loadFromState, saveToState } from "../utils";
import { getProfiles } from "../api/quontxApi";

const LinePlotFilters = ({
  timestampBuckets,
  domains,
  proxies,
  isLoading,
  propagnateData,
}) => {
  const maxDomainCount = 5;
  const propagnateFunctionRef = useRef(propagnateData);

  const [data, setData] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  const [activeDomains, setActiveDomains] = useState([]);
  const [activeProxy, setActiveProxy] = useState(null);

  const [domainSelectDisabled, setDomainSelectDisabled] = useState(false);

  const manageActiveDomains = (selectedDomains) => {
    if (selectedDomains.length <= maxDomainCount) {
      setDomainSelectDisabled(false);
      setActiveDomains(
        selectedDomains.map((domain) => {
          return { label: domain.label, value: domain.value };
        })
      );
      return;
    }
    setDomainSelectDisabled(true);
  };

  const resetFilters = () => {
    resetState("linePlotState", [
      {
        stateFunc: setActiveDomains,
        nullVal: [],
      },
      {
        stateFunc: setActiveProxy,
        nullVal: null,
      },
    ]);
    setDomainSelectDisabled(false);
  };

  const loadProfiles = useCallback(async () => {
    const profiles = await getProfiles({
      itemsPerPage: 1,
      entities: activeProxy.value,
    });
    propagnateFunctionRef.current(profiles.data);

    // Create key value pairs for the data
    profiles.data.data.forEach((profile) => {
      data[profile.metadata.corpora.name] = profile.profile;
    });
    setData(data);
  }, [activeProxy, data]);

  useEffect(() => {
    loadFromState("linePlotState", [
      {
        stateFunc: setActiveDomains,
        storageObjectKey: "activeDomains",
        defaultVal: [],
      },
      {
        stateFunc: setActiveProxy,
        storageObjectKey: "activeProxy",
        defaultVal: null,
      },
    ]);

    const propagnateDataFunctionCopy = propagnateFunctionRef.current;
    return () => {
      propagnateDataFunctionCopy({});
    };
  }, []);

  useEffect(() => {
    if (activeDomains.length > 0 || activeProxy !== null) {
      saveToState("linePlotState", {
        activeDomains: activeDomains,
        activeProxy: activeProxy,
      });
    }
  }, [activeDomains, activeProxy]);

  useEffect(() => {
    if (activeProxy === null) return;
    (async () => {
      setLoadingData(true);
      await loadProfiles();
      setLoadingData(false);
    })();
  }, [activeProxy, loadProfiles]);

  return (
    <div className="w-full flex flex-col items-center content-center">
      {isLoading && <p>Loading data...</p>}
      {!isLoading && data && proxies && domains && (
        <div className="flex flex-col gap-4 items-center justify-center mb-8">
          <div className="flex gap-4  justify-center mb-8">
            <div className="w-[240px] flex flex-col gap-2">
              <p>Organization Proxile</p>
              <Select
                isSearchable={true}
                value={activeProxy}
                onChange={(selected) => setActiveProxy(selected)}
                options={proxies.map((proxy) => {
                  return {
                    label: proxy.name,
                    value: proxy.uuid,
                  };
                })}
              />
            </div>
            <div className="w-[240px] flex flex-col gap-2">
              <p>Societal Domains</p>
              <Select
                isMulti={true}
                isSearchable={true}
                value={activeDomains}
                onChange={(selected) => manageActiveDomains(selected)}
                options={domains.map((domain) => {
                  return {
                    label: domain.description,
                    value: domain.uuid,
                    isDisabled: domainSelectDisabled,
                  };
                })}
              />
            </div>
          </div>
          <div>
            <button
              onClick={() => resetFilters()}
              className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
            >
              Reset filters
            </button>
          </div>
          {activeDomains && activeProxy && data && (
            <div className="flex items-center content-center flex-wrap">
              <LinePlot
                isLoading={loadingData}
                data={data}
                timebuckets={timestampBuckets}
                activeDomains={activeDomains}
                activeProxy={activeProxy}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LinePlotFilters;
